/**
 * API Error Handling Utilities
 * Provides user-friendly error messages for common API failures
 */

export interface ApiErrorInfo {
  /** User-friendly message to display */
  message: string;
  /** Technical details for debugging */
  technical: string;
  /** HTTP status code if available */
  status?: number;
  /** Whether this is a retryable error */
  retryable: boolean;
  /** Suggested action for the user */
  action?: string;
}

/**
 * HTTP status code to user-friendly message mapping
 */
const STATUS_MESSAGES: Record<number, { message: string; action?: string; retryable: boolean }> = {
  400: {
    message: 'Invalid request. Please check your input and try again.',
    retryable: false,
  },
  401: {
    message: 'Your session has expired. Please log in again.',
    action: 'Please refresh the page and log in.',
    retryable: false,
  },
  403: {
    message: 'You don\'t have permission to access this resource.',
    action: 'Contact your administrator if you need access.',
    retryable: false,
  },
  404: {
    message: 'The requested resource was not found.',
    retryable: false,
  },
  405: {
    message: 'This feature is temporarily unavailable.',
    action: 'Please try again later or contact support.',
    retryable: true,
  },
  408: {
    message: 'The request timed out. Please try again.',
    retryable: true,
  },
  429: {
    message: 'Too many requests. Please wait a moment and try again.',
    action: 'Wait a few seconds before retrying.',
    retryable: true,
  },
  500: {
    message: 'A server error occurred. Our team has been notified.',
    action: 'Please try again in a few minutes.',
    retryable: true,
  },
  502: {
    message: 'The server is temporarily unavailable.',
    action: 'Please try again in a few moments.',
    retryable: true,
  },
  503: {
    message: 'The service is temporarily unavailable.',
    action: 'Please try again in a few minutes.',
    retryable: true,
  },
  504: {
    message: 'The request timed out. Please try again.',
    retryable: true,
  },
};

/**
 * Parse an API error and return user-friendly information
 */
export function parseApiError(error: any): ApiErrorInfo {
  const status = error?.response?.status;
  const rawDetail = error?.response?.data?.detail;
  // FastAPI request-validation (422) returns an ARRAY of
  // {loc, msg, type} objects, not a string. Name the offending field from
  // `loc` — "email: value is not a valid email address" is actionable, a bare
  // "value is not a valid email address" is not, and it is the difference
  // between a user fixing their input and filing a ticket.
  const serverMessage = Array.isArray(rawDetail)
    ? rawDetail
        .map((e: any) => {
          const msg = e?.msg || String(e);
          // loc is like ["body", "email"] — the last entry is the field.
          const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : undefined;
          return field && field !== 'body' ? `${field}: ${msg}` : msg;
        })
        .join('; ')
    : rawDetail || error?.response?.data?.message;
  const isNetworkError = error?.message === 'Network Error' || !error?.response;
  const isCorsError = error?.message?.includes('CORS') || error?.message?.includes('cross-origin');

  // Network/CORS errors
  if (isNetworkError || isCorsError) {
    return {
      message: 'Unable to connect to the server.',
      technical: error?.message || 'Network error',
      retryable: true,
      action: 'Please check your internet connection and try again.',
    };
  }

  // Known status codes
  if (status && STATUS_MESSAGES[status]) {
    const statusInfo = STATUS_MESSAGES[status];
    return {
      message: serverMessage || statusInfo.message,
      technical: `HTTP ${status}: ${error?.message || 'Unknown error'}`,
      status,
      retryable: statusInfo.retryable,
      action: statusInfo.action,
    };
  }

  // Unknown status code
  if (status) {
    return {
      message: serverMessage || 'An unexpected error occurred.',
      technical: `HTTP ${status}: ${error?.message || 'Unknown error'}`,
      status,
      retryable: status >= 500,
      action: 'Please try again or contact support if the problem persists.',
    };
  }

  // Generic error
  return {
    message: error?.message || 'An unexpected error occurred.',
    technical: error?.message || 'Unknown error',
    retryable: true,
    action: 'Please try again.',
  };
}

/**
 * Get a user-friendly error message from an API error
 */
export function getApiErrorMessage(error: any): string {
  const info = parseApiError(error);
  return info.message;
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  const info = parseApiError(error);
  return info.retryable;
}

/**
 * Simple in-memory cache for API responses with TTL
 */
export class ApiCache<T> {
  private cache = new Map<string, { data: T; timestamp: number; error?: boolean }>();
  private successTTL: number;
  private errorTTL: number;
  private inFlightRequests = new Map<string, Promise<T>>();

  constructor(successTTLMs: number = 60000, errorTTLMs: number = 10000) {
    this.successTTL = successTTLMs;
    this.errorTTL = errorTTLMs;
  }

  /**
   * Get cached data if valid
   */
  get(key: string): { data: T; isError: boolean } | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const ttl = cached.error ? this.errorTTL : this.successTTL;
    const isValid = Date.now() - cached.timestamp < ttl;

    if (!isValid) {
      this.cache.delete(key);
      return null;
    }

    return { data: cached.data, isError: !!cached.error };
  }

  /**
   * Set cache data
   */
  set(key: string, data: T, isError: boolean = false): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      error: isError,
    });
  }

  /**
   * Execute a request with deduplication (prevents multiple in-flight requests for same key)
   */
  async dedupedRequest(key: string, requestFn: () => Promise<T>): Promise<T> {
    // Check if there's already an in-flight request
    const inFlight = this.inFlightRequests.get(key);
    if (inFlight) {
      return inFlight;
    }

    // Start new request
    const promise = requestFn().finally(() => {
      this.inFlightRequests.delete(key);
    });

    this.inFlightRequests.set(key, promise);
    return promise;
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear cached data for a specific key
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }
}
